import { Grid, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { Building01Icon, IconProps } from '@/components/icons/Building01Icon';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { Users02Icon } from '@/components/icons/Users02Icon';
import { Wallet01Icon } from '@/components/icons/Wallet01Icon';
import { DeliverableCard } from '@/components/layout/DeliverableCard/DeliverableCard';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { ROUTE_KEYS } from '@/navigation/constants';
import { COLORS } from '@/styles/tokens/colors';

import { getAISuggestionTabDescriptions } from './ReviewAISuggestionsFromNotification.utils';
import { useOutstandingAISuggestions } from './useOutstandingAISuggestions';

function SavedAutomatically({ count }: { count: number }) {
  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <CheckIcon size={16} color={COLORS.FUNCTIONAL.SUCCESS[500]} />
      <Typography variant="body-s" color={COLORS.TEXT.SECONDARY}>
        {count} saved automatically
      </Typography>
    </Stack>
  );
}

export interface ReviewAISuggestionsFromNotificationProps {
  isOpen: boolean;
  onClose: () => void;
  householdId: string | null;
}

interface SuggestionItem {
  title: string;
  description: string;
  descriptionContent?: React.ReactNode;
  icon: React.ComponentType<IconProps>;
  onClick: () => void;
}

export function ReviewAISuggestionsFromNotification({
  isOpen,
  onClose,
  householdId,
}: ReviewAISuggestionsFromNotificationProps) {
  const { navigate } = useNavigateToRoute();
  const { householdName, suggestions, loading, autoAcceptedSuggestions } =
    useOutstandingAISuggestions({
      householdId,
    });

  const {
    peopleTabDescription,
    trustsTabDescription,
    businessesTabDescription,
    holdingsTabDescription,
    peopleSavedAutomaticallyCount,
    trustsSavedAutomaticallyCount,
    businessesSavedAutomaticallyCount,
    holdingsSavedAutomaticallyCount,
  } = useMemo(() => {
    return getAISuggestionTabDescriptions(
      suggestions,
      loading,
      autoAcceptedSuggestions
    );
  }, [suggestions, loading, autoAcceptedSuggestions]);

  const suggestionItems: SuggestionItem[] = useMemo(() => {
    const basePeople: SuggestionItem = {
      title: 'People & organizations',
      description: peopleTabDescription,
      descriptionContent:
        peopleSavedAutomaticallyCount > 0 ? (
          <SavedAutomatically count={peopleSavedAutomaticallyCount} />
        ) : undefined,
      icon: Users02Icon,
      onClick: () => {
        if (!householdId) return;
        navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_PEOPLE_ORGANIZATIONS, {
          householdId,
        });
        onClose();
      },
    };

    const holdings: SuggestionItem = {
      title: 'Holdings',
      description: holdingsTabDescription,
      descriptionContent:
        holdingsSavedAutomaticallyCount > 0 ? (
          <SavedAutomatically count={holdingsSavedAutomaticallyCount} />
        ) : undefined,
      icon: Wallet01Icon,
      onClick: () => {
        if (!householdId) return;
        navigate(ROUTE_KEYS.HOUSEHOLD_ESTATE_BALANCE_SHEET, {
          householdId,
        });
        onClose();
      },
    };

    const trusts: SuggestionItem = {
      title: 'Trusts',
      description: trustsTabDescription,
      descriptionContent:
        trustsSavedAutomaticallyCount > 0 ? (
          <SavedAutomatically count={trustsSavedAutomaticallyCount} />
        ) : undefined,
      icon: Building01Icon,
      onClick: () => {
        if (!householdId) return;
        navigate(ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS, {
          householdId,
        });
        onClose();
      },
    };

    const businesses: SuggestionItem = {
      title: 'Businesses',
      description: businessesTabDescription,
      descriptionContent:
        businessesSavedAutomaticallyCount > 0 ? (
          <SavedAutomatically count={businessesSavedAutomaticallyCount} />
        ) : undefined,
      icon: Building01Icon,
      onClick: () => {
        if (!householdId) return;
        navigate(ROUTE_KEYS.HOUSEHOLD_ESTATE_BUSINESSES, {
          householdId,
        });
        onClose();
      },
    };

    return [basePeople, trusts, businesses, holdings];
  }, [
    peopleTabDescription,
    peopleSavedAutomaticallyCount,
    trustsTabDescription,
    trustsSavedAutomaticallyCount,
    businessesTabDescription,
    businessesSavedAutomaticallyCount,
    holdingsTabDescription,
    holdingsSavedAutomaticallyCount,
    householdId,
    navigate,
    onClose,
  ]);

  return (
    <DialogModal
      isOpen={isOpen}
      onClose={onClose}
      heading="What would you like to review?"
      subheading={householdName ?? undefined}
      dialogPaperSx={{
        width: 680,
      }}
      dialogContentSx={{
        minHeight: 160,
        px: 3,
        pb: 3,
      }}
      dialogHeadingContainerSx={{
        p: 3,
      }}
    >
      <Grid container>
        <Grid item container spacing={2}>
          {suggestionItems
            .filter(
              (i) =>
                i.title === 'People & organizations' || i.title === 'Trusts'
            )
            .map((item) => (
              <Grid item md={6} key={item.title}>
                <DeliverableCard
                  title={item.title}
                  description={item.description}
                  descriptionContent={item.descriptionContent}
                  icon={item.icon}
                  sizeVariant="small"
                  onClick={item.onClick}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    height: '100%',
                    minHeight: 120,
                    p: 3,
                  }}
                />
              </Grid>
            ))}
        </Grid>
        <Grid item container spacing={2} sx={{ mt: 0 }}>
          {suggestionItems
            .filter((i) => i.title === 'Businesses' || i.title === 'Holdings')
            .map((item) => (
              <Grid item md={6} key={item.title}>
                <DeliverableCard
                  title={item.title}
                  description={item.description}
                  descriptionContent={item.descriptionContent}
                  icon={item.icon}
                  sizeVariant="small"
                  onClick={item.onClick}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    height: '100%',
                    minHeight: 120,
                    p: 3,
                  }}
                />
              </Grid>
            ))}
        </Grid>
      </Grid>
    </DialogModal>
  );
}
